import { ArrowUpRight } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Reveal, ChapterHeading } from "./Reveal";
import {
    FintechMockup,
    EditorialMockup,
    TerminalMockup,
    ProductMockup,
} from "./mockups";

const PROJECTS = [
    {
        id: "finance",
        title: "Finance Website Example",
        category: "Finance · Business",
        description:
            "An example design for a finance or advisory business: a calm, trustworthy look with a clear path for visitors to get in touch.",
        tags: ["Web Design", "Development", "SEO"],
        metric: "Example design",
        Mockup: FintechMockup,
        tint: "bg-ice",
    },
    {
        id: "interior",
        title: "Interior Studio Example",
        category: "Design · Portfolio",
        description:
            "An example portfolio layout for a creative studio, letting large project images and quiet typography do the talking.",
        tags: ["Art Direction", "Web Design", "CMS"],
        metric: "Example design",
        Mockup: EditorialMockup,
        tint: "bg-cloud",
    },
    {
        id: "saas",
        title: "SaaS Website Example",
        category: "Software · Startup",
        description:
            "An example marketing page for a software product, built for speed and clarity from the first moment it loads.",
        tags: ["Design System", "Development", "Landing Pages"],
        metric: "Example design",
        Mockup: TerminalMockup,
        tint: "bg-frost/60",
    },
    {
        id: "store",
        title: "Online Store Example",
        category: "E-commerce · Retail",
        description:
            "An example product page for an online store, with a clean layout and a simple, quick path to checkout.",
        tags: ["E-commerce", "Web Design"],
        metric: "Example design",
        Mockup: ProductMockup,
        tint: "bg-ice",
    },
];

const ProjectCard = ({ project, index }) => {
    const { Mockup } = project;
    return (
        <Reveal delay={(index % 2) * 0.1}>
            <article
                data-testid={`project-card-${project.id}`}
                className="group overflow-hidden rounded-2xl border border-slate-200 bg-white transition-all duration-500 hover:-translate-y-1.5 hover:border-brand/30 hover:shadow-[0_32px_64px_-32px_rgba(37,99,235,0.22)]"
            >
                <div className={`${project.tint} p-6 sm:p-10`}>
                    <div className="transition-transform duration-500 ease-out group-hover:scale-[1.02] group-hover:-rotate-[0.5deg]">
                        <Mockup />
                    </div>
                </div>
                <div className="p-6 sm:p-8">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-dark">
                                {project.category}
                            </p>
                            <h3 className="mt-2 text-xl font-semibold tracking-tight text-ink sm:text-2xl">
                                {project.title}
                            </h3>
                        </div>
                        <span className="shrink-0 rounded-full bg-ice px-3 py-1.5 text-[11px] font-semibold text-brand-deep">
                            {project.metric}
                        </span>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">
                        {project.description}
                    </p>
                    <div className="mt-5 flex flex-wrap items-center justify-between gap-4 border-t border-slate-100 pt-5">
                        <div className="flex flex-wrap gap-2">
                            {project.tags.map((tag) => (
                                <span
                                    key={tag}
                                    className="rounded-full border border-slate-200 px-3 py-1 text-[11px] font-medium text-slate-500"
                                >
                                    {tag}
                                </span>
                            ))}
                        </div>
                        <button
                            type="button"
                            data-testid={`project-view-${project.id}`}
                            onClick={() =>
                                toast.info(
                                    `${project.title} is a concept design, not client work. Start a project and yours could be the first real one.`
                                )
                            }
                            className="group/btn inline-flex items-center gap-1.5 text-sm font-semibold text-ink transition-colors duration-200 hover:text-brand-dark"
                        >
                            View Project
                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                        </button>
                    </div>
                </div>
            </article>
        </Reveal>
    );
};

const Work = () => (
    <section id="work" data-testid="work-section" className="bg-white py-20 sm:py-28 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-end justify-between gap-6">
                <ChapterHeading
                    testId="work-heading"
                    chapter="01"
                    eyebrow="Example Work"
                    title="Concept designs you can explore."
                    description="These are example projects I designed to show my style and quality. They are not past client work. Your project could be the first real one featured here."
                />
                <Reveal delay={0.2}>
                    <p className="font-mono text-xs uppercase tracking-widest text-slate-400">
                        Concept showcase
                    </p>
                </Reveal>
            </div>
            <div className="mt-14 grid grid-cols-1 gap-8 sm:mt-16 sm:gap-10 lg:grid-cols-2">
                {PROJECTS.map((project, i) => (
                    <ProjectCard key={project.id} project={project} index={i} />
                ))}
            </div>
        </div>
    </section>
);

export default Work;
